import {
  faMagnifyingGlass,
  faCalendarPlus,
  faCalendarCheck,
  faUsers,
  faStethoscope,
  faEdit,
  faTrash,
  faPrint,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';
import NotificationModal from '../components/NotificationModal';
import StatsCard from '../components/StatsCard';
import useConsultas from '../hooks/useConsultas';
import Button from '../components/Button';
import Input from '../components/Input';
import PageHeader from '../components/PageHeader';
import IconButton from '../components/IconButton';
import DateRangeFilter, { DateRange, FilterPreset } from '../components/DateRangeFilter';
import { Consulta } from '../types';

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

export default function Consultas() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    consultas,
    totalPages,
    totalConsultas,
    consultasHoje,
    pacientesUnicos,
    loading,
    error,
    get,
    getStats,
    deleteById
  } = useConsultas();
  const [page, setPage] = useState<number>(0);
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [deleteConsultaId, setDeleteConsultaId] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState | null>(null);

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate')
  });
  const [activeFilter, setActiveFilter] = useState<FilterPreset>(
    (searchParams.get('filter') as FilterPreset) || null
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    get(page, 10, debouncedQuery, dateRange.startDate || undefined, dateRange.endDate || undefined);
    getStats(
      debouncedQuery || undefined,
      dateRange.startDate || undefined,
      dateRange.endDate || undefined
    );
  }, [get, getStats, page, debouncedQuery, dateRange]);

  const handleDeleteConsulta = async (consultaId: string): Promise<void> => {
    try {
      await deleteById(consultaId);
      setDeleteConsultaId(null);
      setNotification({
        type: 'success',
        title: 'Consulta Removida',
        message: 'A consulta foi removida com sucesso do sistema.'
      });
      await get(
        page,
        10,
        debouncedQuery,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      await getStats(
        debouncedQuery || undefined,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
    } catch (error) {
      setDeleteConsultaId(null);
      setNotification({
        type: 'error',
        title: 'Erro ao Remover Consulta',
        message: (error as Error).message || 'Ocorreu um erro inesperado. Tente novamente.'
      });
    }
  };

  function handleAddClick(): void {
    navigate('/consultas/create');
  }

  function handlePageClick(selectedPage: number): void {
    if (selectedPage < 0 || selectedPage >= totalPages) return;
    setPage(selectedPage);
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  };

  const handleDateRangeChange = (newDateRange: DateRange, preset: FilterPreset): void => {
    setDateRange(newDateRange);
    setActiveFilter(preset);
    setPage(0);

    const newSearchParams = new URLSearchParams();
    if (newDateRange.startDate) newSearchParams.set('startDate', newDateRange.startDate);
    if (newDateRange.endDate) newSearchParams.set('endDate', newDateRange.endDate);
    if (preset) newSearchParams.set('filter', preset);

    setSearchParams(newSearchParams);
  };

  const handleClearDateFilter = (): void => {
    setDateRange({ startDate: null, endDate: null });
    setActiveFilter(null);
    setPage(0);

    const newSearchParams = new URLSearchParams();
    setSearchParams(newSearchParams);
  };

  if (error) {
    return (
      <main className="w-full flex-grow p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Erro ao carregar consultas: {error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto">
        <PageHeader title="Consultas" subtitle="Gerenciamento de consultas oftalmológicas" />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title={debouncedQuery ? 'Consultas (Filtro)' : 'Total de Consultas'}
            value={loading ? '...' : totalConsultas}
            icon={faCalendarCheck}
            color="blue"
          />
          <StatsCard
            title={debouncedQuery ? 'Hoje (Filtro)' : 'Consultas Hoje'}
            value={loading ? '...' : consultasHoje}
            icon={faCalendar}
            color="green"
          />
          <StatsCard
            title={debouncedQuery ? 'Pacientes (Filtro)' : 'Pacientes Únicos'}
            value={loading ? '...' : pacientesUnicos}
            icon={faUsers}
            color="purple"
          />
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <Button
              onClick={handleAddClick}
              variant="primary"
              size="lg"
              fullWidth
              leftIcon={faCalendarPlus}
            >
              Nova Consulta
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <DateRangeFilter
          dateRange={dateRange}
          activeFilter={activeFilter}
          onDateRangeChange={handleDateRangeChange}
          onClearFilter={handleClearDateFilter}
        />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                />
                <Input
                  variant="search"
                  type="search"
                  placeholder="Buscar por nome do paciente..."
                  value={query || ''}
                  onChange={handleQueryChange}
                  leftIcon={faMagnifyingGlass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Consultas List */}
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Lista de Consultas</h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <img className="w-12 h-12" src="/loading.svg" alt="loading" />
            </div>
          ) : consultas && consultas.length > 0 ? (
            <>
              <div className="divide-y divide-gray-200">
                {(consultas as Consulta[]).map(consulta => (
                  <div key={consulta.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="bg-green-100 p-3 rounded-full flex-shrink-0">
                          <FontAwesomeIcon
                            icon={faStethoscope}
                            className="w-6 h-6 text-green-600"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-4">
                            <div>
                              <p className="text-lg font-medium text-gray-900 truncate">
                                {consulta.paciente?.nome || 'Paciente não identificado'}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <div className="flex items-center space-x-1">
                                  <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                                  <span>
                                    {new Date(consulta.created_at as string).toLocaleDateString(
                                      'pt-BR'
                                    )}
                                  </span>
                                </div>
                                {consulta.queixa && (
                                  <div className="flex items-center space-x-1">
                                    <span className="truncate max-w-md">
                                      Queixa: {consulta.queixa.substring(0, 50)}
                                      {consulta.queixa.length > 50 ? '...' : ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <IconButton
                          onClick={() => navigate(`/consultas/${consulta.id}`)}
                          icon={faEdit}
                          variant="primary"
                          tooltip="Visualizar/Editar consulta"
                        />
                        <IconButton
                          onClick={() => navigate(`/consultas/print/${consulta.id}`)}
                          icon={faPrint}
                          variant="warning"
                          tooltip="Imprimir consulta"
                        />
                        <IconButton
                          onClick={() => setDeleteConsultaId(consulta.id)}
                          icon={faTrash}
                          variant="danger"
                          tooltip="Remover consulta"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Página {page + 1} de {totalPages}
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageClick(page - 1)}
                        disabled={page === 0}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageClick(page + 1)}
                        disabled={page >= totalPages - 1}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faCalendarCheck} className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">
                {query ? 'Nenhuma consulta encontrada para a busca' : 'Nenhuma consulta cadastrada'}
              </p>
              <Button onClick={handleAddClick} variant="outline">
                Cadastrar primeira consulta
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {deleteConsultaId && (
        <ConfirmationDialog
          title="Remover Consulta"
          message="Tem certeza que deseja remover esta consulta? Esta ação não pode ser desfeita."
          onConfirm={() => handleDeleteConsulta(deleteConsultaId)}
          onCancel={() => setDeleteConsultaId(null)}
          confirmText="Remover"
          cancelText="Cancelar"
          type="danger"
        />
      )}

      {/* Notification Modal */}
      {notification && (
        <NotificationModal
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </main>
  );
}
